/**
 * Shared unlock / perk catalog for office decor.
 * Used by client UI and server decor validation.
 * `id` must be a MapFurnitureKind so the world can place/draw it.
 */
import { isMapFurnitureKind, type MapFurnitureKind } from "@/lib/office/personal-office-layout";

export const OFFICE_UNLOCKS = [
  {
    id: "plant",
    name: "Desk Plant",
    desc: "A little green for your desk",
    pts: 25,
    swatch: "#2EA86A",
  },
  {
    id: "cactus",
    name: "Desert Cactus",
    desc: "Low maintenance, high vibes",
    pts: 40,
    swatch: "#3D8B5F",
  },
  {
    id: "lamp",
    name: "Desk Lamp",
    desc: "Warm glow for late standups",
    pts: 55,
    swatch: "#E8A93C",
  },
  {
    id: "sign",
    name: "Neon VYLLAGE Sign",
    desc: "Lounge wall decor",
    pts: 70,
    swatch: "#2D9BD6",
  },
  {
    id: "coffee",
    name: "Espresso Cart",
    desc: "Fuel for the next sprint",
    pts: 85,
    swatch: "#5C4033",
  },
  {
    id: "fox",
    name: "Atlas Fox Statue",
    desc: "The office mascot, in pixel form",
    pts: 100,
    swatch: "#001847",
  },
  {
    id: "bookshelf",
    name: "Design Library",
    desc: "A tall stack of good ideas",
    pts: 120,
    swatch: "#64479D",
  },
  {
    id: "neon",
    name: "Open Neon",
    desc: "Always open energy",
    pts: 140,
    swatch: "#FF6B9D",
  },
  {
    id: "arcade",
    name: "Arcade Cabinet",
    desc: "Game corner for the lounge",
    pts: 160,
    swatch: "#5461AD",
  },
  {
    id: "guitar",
    name: "Lounge Guitar",
    desc: "Acoustic break-time hero",
    pts: 180,
    swatch: "#C9A227",
  },
  {
    id: "fishbowl",
    name: "Pixel Fishbowl",
    desc: "A tiny swimming buddy",
    pts: 200,
    swatch: "#2D9BD6",
  },
  {
    id: "jukebox",
    name: "Pixel Jukebox",
    desc: "Queue the lobby bops",
    pts: 220,
    swatch: "#D8443C",
  },
  {
    id: "trophy",
    name: "Golden Fox Trophy",
    desc: "Top collaborator award",
    pts: 250,
    swatch: "#E8A93C",
  },
  {
    id: "statue",
    name: "Hero Bust",
    desc: "Commemorate the ship",
    pts: 300,
    swatch: "#8894A8",
  },
] as const;

export type OfficeUnlockId = (typeof OFFICE_UNLOCKS)[number]["id"];

export const OFFICE_UNLOCK_IDS = OFFICE_UNLOCKS.map((u) => u.id) as [
  OfficeUnlockId,
  ...OfficeUnlockId[],
];

/** Map unlock id → furniture kind for world placement (same string when possible). */
export function decorIdToFurnitureKind(id: string): MapFurnitureKind {
  if (isMapFurnitureKind(id)) return id;
  return "plant";
}

export function unlockSwatch(id: string): string {
  const u = OFFICE_UNLOCKS.find((x) => x.id === id);
  return u?.swatch ?? "#2EA86A";
}

export { DEFAULT_AVATAR_COLORS, DEFAULT_AVATAR_LOOK } from "@/lib/office/avatar-look";

/** Unlock ids a user has earned from points (and any already stored). */
export function unlocksEarnedFromPoints(points: number, existing: string[] = []): string[] {
  const set = new Set(existing);
  for (const u of OFFICE_UNLOCKS) {
    if (points >= u.pts) set.add(u.id);
  }
  return [...set];
}

/** True if every decor id is a known unlock the user has earned. */
export function canPlaceDecor(decorIds: string[], unlockedIds: string[]): boolean {
  const allowed = new Set(unlockedIds);
  for (const id of decorIds) {
    if (!OFFICE_UNLOCK_IDS.includes(id as OfficeUnlockId)) return false;
    if (!allowed.has(id)) return false;
  }
  return true;
}
